// Documentação das Edge Functions do sistema Altus Food

export interface EdgeFunctionParam {
  name: string;
  type: string;
  description: string;
  required: boolean;
}

export interface EdgeFunctionResponse {
  status: number;
  description: string;
  example?: string;
}

export interface EdgeFunctionDoc {
  name: string;
  path: string;
  description: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  endpoint: string;
  params?: EdgeFunctionParam[];
  body?: EdgeFunctionParam[];
  responses: EdgeFunctionResponse[];
  requiresAuth: boolean;
  requiresAdmin?: boolean;
  category: string;
}

export const EDGE_FUNCTION_CATEGORIES = [
  { id: "users", name: "Usuários", icon: "Users" },
  { id: "team", name: "Equipe", icon: "UserPlus" },
  { id: "fiscal", name: "Fiscal", icon: "Receipt" },
  { id: "printing", name: "Impressão", icon: "Printer" },
  { id: "notifications", name: "Notificações", icon: "Bell" },
];

export const EDGE_FUNCTIONS: EdgeFunctionDoc[] = [
  // ========== USUÁRIOS ==========
  {
    name: "create-user",
    path: "supabase/functions/create-user/index.ts",
    description: "Cria um novo usuário no auth.users do Supabase. Usado pelo Super Admin para criar usuários sem passar pelo fluxo de signup.",
    method: "POST",
    endpoint: "/create-user",
    body: [
      { name: "email", type: "string", description: "Email do usuário", required: true },
      { name: "password", type: "string", description: "Senha inicial", required: true },
      { name: "full_name", type: "string", description: "Nome completo", required: false },
      { name: "restaurant_id", type: "string", description: "ID do restaurante", required: false },
      { name: "role", type: "string", description: "Papel no restaurante", required: false },
    ],
    responses: [
      { status: 200, description: "Usuário criado com sucesso", example: '{ "user": { "id": "xxx", "email": "..." } }' },
      { status: 400, description: "Dados inválidos" },
      { status: 401, description: "Não autorizado" },
      { status: 409, description: "Email já existe" },
    ],
    requiresAuth: true,
    requiresAdmin: true,
    category: "users",
  },
  {
    name: "update-user",
    path: "supabase/functions/update-user/index.ts",
    description: "Atualiza dados de um usuário existente (email, senha, metadata).",
    method: "POST",
    endpoint: "/update-user",
    body: [
      { name: "user_id", type: "string", description: "ID do usuário", required: true },
      { name: "email", type: "string", description: "Novo email", required: false },
      { name: "password", type: "string", description: "Nova senha", required: false },
      { name: "user_metadata", type: "object", description: "Metadata do usuário", required: false },
    ],
    responses: [
      { status: 200, description: "Usuário atualizado" },
      { status: 400, description: "Dados inválidos" },
      { status: 404, description: "Usuário não encontrado" },
    ],
    requiresAuth: true,
    requiresAdmin: true,
    category: "users",
  },
  {
    name: "list-users",
    path: "supabase/functions/list-users/index.ts",
    description: "Lista todos os usuários da plataforma. Usado pelo Super Admin.",
    method: "GET",
    endpoint: "/list-users",
    params: [
      { name: "page", type: "number", description: "Página (default: 1)", required: false },
      { name: "per_page", type: "number", description: "Itens por página (default: 50)", required: false },
    ],
    responses: [
      { status: 200, description: "Lista de usuários", example: '{ "users": [...], "total": 100 }' },
    ],
    requiresAuth: true,
    requiresAdmin: true,
    category: "users",
  },
  {
    name: "reset-user-password",
    path: "supabase/functions/reset-user-password/index.ts",
    description: "Reseta a senha de um usuário para uma nova senha definida.",
    method: "POST",
    endpoint: "/reset-user-password",
    body: [
      { name: "user_id", type: "string", description: "ID do usuário", required: true },
      { name: "new_password", type: "string", description: "Nova senha", required: true },
    ],
    responses: [
      { status: 200, description: "Senha alterada" },
      { status: 400, description: "Senha inválida (mín 6 caracteres)" },
      { status: 404, description: "Usuário não encontrado" },
    ],
    requiresAuth: true,
    requiresAdmin: true,
    category: "users",
  },

  // ========== EQUIPE ==========
  {
    name: "invite-team-member",
    path: "supabase/functions/invite-team-member/index.ts",
    description: "Envia convite por email para novo membro da equipe do restaurante.",
    method: "POST",
    endpoint: "/invite-team-member",
    body: [
      { name: "email", type: "string", description: "Email do convidado", required: true },
      { name: "restaurant_id", type: "string", description: "ID do restaurante", required: true },
      { name: "role", type: "string", description: "Papel: manager, cashier, waiter, kitchen", required: true },
      { name: "full_name", type: "string", description: "Nome do convidado", required: false },
    ],
    responses: [
      { status: 200, description: "Convite enviado" },
      { status: 400, description: "Email inválido ou já é membro" },
      { status: 403, description: "Sem permissão para convidar" },
    ],
    requiresAuth: true,
    category: "team",
  },
  {
    name: "update-team-member",
    path: "supabase/functions/update-team-member/index.ts",
    description: "Atualiza o papel de um membro da equipe.",
    method: "POST",
    endpoint: "/update-team-member",
    body: [
      { name: "user_id", type: "string", description: "ID do usuário", required: true },
      { name: "restaurant_id", type: "string", description: "ID do restaurante", required: true },
      { name: "role", type: "string", description: "Novo papel", required: true },
      { name: "is_active", type: "boolean", description: "Ativo/inativo", required: false },
    ],
    responses: [
      { status: 200, description: "Membro atualizado" },
      { status: 404, description: "Membro não encontrado" },
    ],
    requiresAuth: true,
    category: "team",
  },
  {
    name: "get-team-emails",
    path: "supabase/functions/get-team-emails/index.ts",
    description: "Busca emails dos membros da equipe (para notificações).",
    method: "GET",
    endpoint: "/get-team-emails",
    params: [
      { name: "restaurant_id", type: "string", description: "ID do restaurante", required: true },
      { name: "roles", type: "string", description: "Papéis separados por vírgula", required: false },
    ],
    responses: [
      { status: 200, description: "Lista de emails", example: '{ "emails": ["a@x.com", "b@x.com"] }' },
    ],
    requiresAuth: true,
    category: "team",
  },

  // ========== FISCAL ==========
  {
    name: "emit-nfce-acbr",
    path: "supabase/functions/emit-nfce-acbr/index.ts",
    description: "Emite NFC-e via integração com ACBrMonitor. Conecta via FRP Tunnel ao computador local do restaurante.",
    method: "POST",
    endpoint: "/emit-nfce-acbr",
    body: [
      { name: "order_id", type: "string", description: "ID do pedido", required: true },
      { name: "cpf_consumidor", type: "string", description: "CPF do consumidor (opcional)", required: false },
    ],
    responses: [
      { status: 200, description: "NFC-e emitida", example: '{ "success": true, "chave_acesso": "...", "numero": 123 }' },
      { status: 400, description: "Dados do pedido incompletos" },
      { status: 500, description: "Erro na comunicação com ACBr" },
      { status: 503, description: "ACBrMonitor indisponível" },
    ],
    requiresAuth: true,
    category: "fiscal",
  },
  {
    name: "test-acbr-connection",
    path: "supabase/functions/test-acbr-connection/index.ts",
    description: "Testa conexão TCP com ACBrMonitor via FRP Tunnel.",
    method: "POST",
    endpoint: "/test-acbr-connection",
    body: [
      { name: "restaurant_id", type: "string", description: "ID do restaurante", required: true },
    ],
    responses: [
      { status: 200, description: "Conexão OK", example: '{ "connected": true, "version": "ACBrMonitorPLUS 2.0.0" }' },
      { status: 500, description: "Falha na conexão", example: '{ "connected": false, "error": "Connection refused" }' },
    ],
    requiresAuth: true,
    category: "fiscal",
  },

  // ========== IMPRESSÃO ==========
  {
    name: "qz-sign",
    path: "supabase/functions/qz-sign/index.ts",
    description: "Assina requisições do QZ Tray para impressão segura. Usa certificado digital configurado.",
    method: "POST",
    endpoint: "/qz-sign",
    body: [
      { name: "request", type: "string", description: "Request a ser assinado", required: true },
    ],
    responses: [
      { status: 200, description: "Assinatura gerada", example: '{ "signature": "base64..." }' },
      { status: 400, description: "Request inválido" },
      { status: 500, description: "Erro ao assinar (certificado não configurado)" },
    ],
    requiresAuth: false,
    category: "printing",
  },

  // ========== NOTIFICAÇÕES ==========
  {
    name: "send-push-notification",
    path: "supabase/functions/send-push-notification/index.ts",
    description: "Envia notificação push via Web Push API para navegadores inscritos.",
    method: "POST",
    endpoint: "/send-push-notification",
    body: [
      { name: "user_id", type: "string", description: "ID do usuário destinatário", required: true },
      { name: "title", type: "string", description: "Título da notificação", required: true },
      { name: "body", type: "string", description: "Corpo da notificação", required: true },
      { name: "url", type: "string", description: "URL ao clicar", required: false },
      { name: "data", type: "object", description: "Dados adicionais", required: false },
    ],
    responses: [
      { status: 200, description: "Notificação enviada" },
      { status: 404, description: "Usuário não tem subscription ativa" },
    ],
    requiresAuth: true,
    category: "notifications",
  },
  {
    name: "send-welcome-email",
    path: "supabase/functions/send-welcome-email/index.ts",
    description: "Envia email de boas-vindas para novos usuários.",
    method: "POST",
    endpoint: "/send-welcome-email",
    body: [
      { name: "email", type: "string", description: "Email do destinatário", required: true },
      { name: "name", type: "string", description: "Nome do usuário", required: true },
      { name: "restaurant_name", type: "string", description: "Nome do restaurante", required: false },
    ],
    responses: [
      { status: 200, description: "Email enviado" },
      { status: 400, description: "Email inválido" },
    ],
    requiresAuth: true,
    category: "notifications",
  },
  {
    name: "send-whatsapp-notification",
    path: "supabase/functions/send-whatsapp-notification/index.ts",
    description: "Envia notificação via WhatsApp Business API (Evolution API).",
    method: "POST",
    endpoint: "/send-whatsapp-notification",
    body: [
      { name: "phone", type: "string", description: "Número com DDI (ex: 5511999999999)", required: true },
      { name: "message", type: "string", description: "Mensagem de texto", required: true },
      { name: "restaurant_id", type: "string", description: "ID do restaurante", required: true },
    ],
    responses: [
      { status: 200, description: "Mensagem enviada" },
      { status: 400, description: "Número inválido" },
      { status: 503, description: "WhatsApp não configurado" },
    ],
    requiresAuth: true,
    category: "notifications",
  },
];

// Helper para buscar functions por categoria
export function getEdgeFunctionsByCategory(categoryId: string): EdgeFunctionDoc[] {
  return EDGE_FUNCTIONS.filter(f => f.category === categoryId);
}

// Helper para buscar function por nome
export function getEdgeFunctionByName(name: string): EdgeFunctionDoc | undefined {
  return EDGE_FUNCTIONS.find(f => f.name === name);
}
